import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["button", "fieldsForComponent"];
  static values = { zonesUrl: String, businessPartnersUrl: String };

  async addAllZones() {
    const labelSpan = this.buttonTarget.querySelector('.btn__label');
    const originalText = labelSpan.textContent;

    try {
      // Désactiver le bouton et changer le texte pendant le chargement
      this.buttonTarget.disabled = true;
      labelSpan.textContent = "Chargement...";

      // Récupérer toutes les zones disponibles
      const response = await fetch(this.zonesUrlValue);
      const allZones = await response.json();

      // Récupérer les zones déjà assignées
      const existingZoneIds = this.getExistingZoneIds();

      // Filtrer les zones qui ne sont pas encore assignées
      const zonesToAdd = allZones.filter(zone => !existingZoneIds.includes(zone.id));

      // Ajouter un nested form pour chaque zone manquante
      for (const zone of zonesToAdd) {
        this.addZoneForm(zone);
      }
    } catch (error) {
      this.buttonTarget.disabled = false;
      labelSpan.textContent = originalText;
      console.error("Erreur lors de l'ajout des zones:", error);
    } finally {
      // Réactiver le bouton et remettre le texte original
      this.buttonTarget.disabled = false;
      labelSpan.textContent = originalText;
    }
  }

  async addAllBusinessPartners(event) {
    const clickedButton = event.currentTarget;
    const labelSpan = clickedButton.querySelector('.btn__label');
    const originalText = labelSpan.textContent;

    try {
      // Désactiver le bouton et changer le texte pendant le chargement
      clickedButton.disabled = true;
      labelSpan.textContent = "Chargement...";

      // Récupérer tous les business partners disponibles
      const response = await fetch(this.businessPartnersUrlValue);
      const allPartners = await response.json();

      // Récupérer les partenaires déjà assignés dans la zone courante
      const existingPartnerIds = this.getExistingBusinessPartnerIds(clickedButton);

      // Filtrer les partenaires qui ne sont pas encore assignés
      const partnersToAdd = allPartners.filter(partner => !existingPartnerIds.includes(partner.id));

      // Ajouter les partenaires manquants à la zone courante
      this.addBusinessPartnersToCurrentZone(clickedButton, partnersToAdd);
    } catch (error) {
      clickedButton.disabled = false;
      labelSpan.textContent = originalText;
      console.error("Erreur lors de l'ajout des partenaires:", error);
    } finally {
      // Réactiver le bouton et remettre le texte original
      clickedButton.disabled = false;
      labelSpan.textContent = originalText;
    }
  }

  addZoneForm(zone) {
    // Utiliser les targets existants du composant fields_for
    const template = this.fieldsForComponentTarget.querySelector('[data-form--fields-for--component-target="template"]');
    const container = this.fieldsForComponentTarget.querySelector('[data-form--fields-for--component-target="container"]');

    if (!template || !container) {
      throw new Error("Template ou container non trouvé dans le composant fields_for");
    }

    // Créer un nouveau container pour le nested form
    const div = document.createElement("div");
    div.className = 'nested-form-container';
    div.setAttribute("id", Date.now() + Math.random());

    // Copier le contenu du template et remplacer CHILD_INDEX
    const templateContent = template.innerHTML.replace(/CHILD_INDEX/g, (Date.now() + Math.random()).toString());
    div.innerHTML = templateContent;

    // Préselectionner la zone dans le select
    const zoneSelect = div.querySelector('.service-zone-input');
    if (zoneSelect) {
      const initialValue = [{value: zone.id, text: zone.name}];
      zoneSelect.setAttribute('data-form--collection-select--component-initial-value-value', JSON.stringify(initialValue));
    }
    container.appendChild(div);

    // Déclencher l'événement de changement pour mettre à jour l'interface
    const event = new Event('change');
    this.fieldsForComponentTarget.dispatchEvent(event);
  }

  getExistingZoneIds() {
    // Récupérer tous les selects de zones existants
    const zoneSelects = this.fieldsForComponentTarget.querySelectorAll('.service-zone-input');
    const existingIds = [];

    zoneSelects.forEach(select => {
      // Vérifier que la forme n'est pas marquée pour destruction
      const destroyInput = select.closest('.nested-form-container')?.querySelector('input[name*="[_destroy]"]');
      const isDestroyed = destroyInput && ["true", "1"].includes(destroyInput.value?.toLowerCase());
      const value = select['form--collection-select--component'].value;

      if (value && !isDestroyed) {
        existingIds.push(value);
      }
    });
    return existingIds;
  }

  getExistingBusinessPartnerIds(clickedButton) {
    // Trouver le bouton qui a été cliqué et sa zone parente
    const currentZoneContainer = clickedButton.closest('.nested-form-container');
    if (!currentZoneContainer) return [];

    // Récupérer le select des business partners dans cette zone
    const businessPartnersSelect = currentZoneContainer.querySelector('.service-bp-input');
    if (!businessPartnersSelect) return [];

    // Retourner les IDs déjà sélectionnés
    return Array.from(businessPartnersSelect['form--collection-select--component'].value).map(option => parseInt(option.value)).filter(id => !isNaN(id));
  }

  addBusinessPartnersToCurrentZone(clickedButton, partnersToAdd) {
    // Trouver le bouton qui a été cliqué et sa zone parente
    const currentZoneContainer = clickedButton.closest('.nested-form-container');
    if (!currentZoneContainer) return;

    // Récupérer le select des business partners dans cette zone
    const businessPartnersSelect = currentZoneContainer.querySelector('.service-bp-input');
    if (!businessPartnersSelect) return;

    // Accéder au controller du composant collection-select
    const selectController = businessPartnersSelect['form--collection-select--component'];
    if (!selectController) return;

    // Ajouter chaque partenaire manquant
    partnersToAdd.forEach(partner => {
      selectController.addValue({ value: partner.id, text: partner.name });
    });
    selectController.updatePrompt();
  }
}
